import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";

export const GlobalContext = createContext(null);

const DEFAULT_ORDER_TYPE = "home_delivery";

const USER_STORAGE_KEY = "customer";

/* =====================================================
   SESSION STORAGE HELPERS
===================================================== */

const getSessionValue = (key, fallbackValue = "") => {
  try {
    return sessionStorage.getItem(key) ?? fallbackValue;
  } catch {
    return fallbackValue;
  }
};

const getStoredRestaurant = () => {
  try {
    const savedRestaurant = sessionStorage.getItem("selectedRestaurant");

    if (!savedRestaurant) {
      return null;
    }

    return JSON.parse(savedRestaurant);
  } catch (error) {
    console.error("Selected restaurant parsing error:", error);

    try {
      sessionStorage.removeItem("selectedRestaurant");
    } catch {
      // Session storage unavailable.
    }

    return null;
  }
};

const normalizeSelectedRestaurant = (restaurant) => {
  if (!restaurant || typeof restaurant !== "object") {
    return null;
  }

  const userId = String(
    restaurant?.userId ||
      restaurant?.userid ||
      restaurant?.user_id ||
      restaurant?.restaurantUserId ||
      restaurant?.restaurantId ||
      restaurant?.code ||
      "",
  ).trim();

  if (!userId) {
    return null;
  }

  const rawStartingPrice =
    restaurant?.startingPrice ?? restaurant?.starting_price;

  const parsedStartingPrice = Number(rawStartingPrice);

  return {
    userId,

    name: String(
      restaurant?.name ||
        restaurant?.restaurantName ||
        restaurant?.restaurant_name ||
        "Restaurant",
    ).trim(),

    image:
      restaurant?.image ||
      restaurant?.restaurantImage ||
      restaurant?.restaurant_image ||
      "",

    address: String(restaurant?.address || restaurant?.street || "").trim(),

    phone: String(
      restaurant?.phone || restaurant?.restaurant_phone || "",
    ).trim(),

    city: String(restaurant?.city || "").trim(),

    state: String(restaurant?.state || "").trim(),

    latitude: restaurant?.latitude ?? "",

    longitude: restaurant?.longitude ?? restaurant?.lontitude ?? "",

    servingType: String(
      restaurant?.servingType || restaurant?.serving_type || "",
    )
      .trim()
      .toUpperCase(),

    startingPrice: Number.isFinite(parsedStartingPrice)
      ? parsedStartingPrice
      : null,

    customerLatitude: restaurant?.customerLatitude ?? "",

    customerLongitude: restaurant?.customerLongitude ?? "",

    selectedItemName: String(restaurant?.selectedItemName || "").trim(),
  };
};

/* =====================================================
   AUTH STORAGE HELPERS
===================================================== */

const getStoredUser = () => {
  try {
    const savedUser = sessionStorage.getItem(USER_STORAGE_KEY);

    if (!savedUser) {
      return null;
    }

    const parsedUser = JSON.parse(savedUser);

    if (
      parsedUser &&
      typeof parsedUser === "object" &&
      (parsedUser.user_code || parsedUser.number)
    ) {
      return parsedUser;
    }

    return null;
  } catch (error) {
    console.error("Stored user parsing error:", error);

    try {
      sessionStorage.removeItem(USER_STORAGE_KEY);
    } catch {
      // Session storage unavailable.
    }

    return null;
  }
};

const saveStoredUser = (userData) => {
  try {
    const duplicateCustomerKeys = [
      "chaafoUser",
      "chaafo_user",
      "user_code",
      "first_name",
      "last_name",
      "number",
      "phone",
      "privacy_policy",
      "email",
      "username",
      "gender",
      "age",
      "date_of_birth",
      "profile_image",
    ];

    duplicateCustomerKeys.forEach((key) => {
      sessionStorage.removeItem(key);
    });

    if (!userData) {
      sessionStorage.removeItem(USER_STORAGE_KEY);
      return;
    }

    sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
  } catch (error) {
    console.error("Unable to save user session:", error);
  }
};

/* =====================================================
   GLOBAL PROVIDER
===================================================== */

export const GlobalProvider = ({ children }) => {
  /* =====================================================
     ENV CONFIG
  ===================================================== */

  const baseUrl =
    process.env.REACT_APP_ANLOOK_API_BASE_URL || "https://api.anlook.com/api";

  const salesUsername = process.env.REACT_APP_API_AUTH_USERNAME || "";

  const salesPassword = process.env.REACT_APP_API_AUTH_PASSWORD || "";

  const appName = process.env.REACT_APP_APP_NAME || "Chaafo";

  const packkyBaseUrl = process.env.REACT_APP_PACKKY_API_BASE_URL || "";

  const packkyUsername = process.env.REACT_APP_PACKKY_API_AUTH_USERNAME || "";

  const packkyPassword = process.env.REACT_APP_PACKKY_API_AUTH_PASSWORD || "";

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  const anlookBaseUrl = process.env.REACT_APP_ANLOOK_API_BASE_URL || "";

  const anlookUsername = process.env.REACT_APP_ANLOOK_API_AUTH_USERNAME || "";

  const anlookPassword = process.env.REACT_APP_ANLOOK_API_AUTH_PASSWORD || "";

  const anlookImageBaseUrl =
    process.env.REACT_APP_ANLOOK_IMAGE_API_BASE_URL || "";

  const anlookImageUsername =
    process.env.REACT_APP_ANLOOK_IMAGE_API_AUTH_USERNAME || "";

  const anlookImagePassword =
    process.env.REACT_APP_ANLOOK_IMAGE_API_AUTH_PASSWORD || "";

  /* =====================================================
     AUTH STATE
  ===================================================== */

  const [user, setAuthUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const storedUser = getStoredUser();

    setAuthUser(storedUser);
    setAuthLoading(false);
  }, []);

  /*
   * Use this setUser everywhere after login or
   * when updating profile details.
   *
   * It updates both Context and sessionStorage.
   */
  const setUser = useCallback((nextUser) => {
    setAuthUser((previousUser) => {
      const resolvedUser =
        typeof nextUser === "function" ? nextUser(previousUser) : nextUser;

      saveStoredUser(resolvedUser);

      return resolvedUser;
    });
  }, []);

  const logoutUser = useCallback(() => {
    saveStoredUser(null);
    setAuthUser(null);
  }, []);

  const isAuthenticated = Boolean(user?.user_code || user?.number);

  /* =====================================================
     CATEGORY STATE
  ===================================================== */
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState("");

  const selectedCategoryIds = useMemo(() => {
    if (selectedCategoryId === null || selectedCategoryId === undefined) {
      return [];
    }

    return [selectedCategoryId];
  }, [selectedCategoryId]);

  const selectCategory = useCallback((categoryId, categoryName = "") => {
    const numericCategoryId = Number(categoryId);

    if (!Number.isFinite(numericCategoryId)) {
      return;
    }

    setSelectedCategoryId(numericCategoryId);
    setSelectedCategoryName(String(categoryName || "").trim());
  }, []);

  const clearSelectedCategory = useCallback(() => {
    setSelectedCategoryId(null);
    setSelectedCategoryName("");
  }, []);

  /* =====================================================
     SELECTED RESTAURANT
  ===================================================== */

  const [selectedRestaurant, setSelectedRestaurant] =
    useState(getStoredRestaurant);

const selectRestaurant = useCallback((restaurant) => {
  const cleanRestaurant = normalizeSelectedRestaurant(restaurant);

  if (!cleanRestaurant) {
    return;
  }

  setSelectedRestaurant(cleanRestaurant);

  try {
    sessionStorage.setItem(
      "selectedRestaurant",
      JSON.stringify(cleanRestaurant),
    );
  } catch (error) {
    console.error("Unable to save selected restaurant:", error);
  }
}, []);

  const clearSelectedRestaurant = useCallback(() => {
    setSelectedRestaurant(null);

    try {
      sessionStorage.removeItem("selectedRestaurant");
    } catch {
      // Session storage unavailable.
    }
  }, []);

 const [selectedDeliveryAddress, setSelectedDeliveryAddressState] = useState(
   () => {
     try {
       return sessionStorage.getItem("selectedDeliveryAddress") || "";
     } catch {
       return "";
     }
   },
 );

const [selectedDeliveryLocation, setSelectedDeliveryLocationState] = useState(
  () => {
    try {
      return {
        latitude: sessionStorage.getItem("selectedDeliveryLatitude") || "",
        longitude: sessionStorage.getItem("selectedDeliveryLongitude") || "",
      };
    } catch {
      return {
        latitude: "",
        longitude: "",
      };
    }
  },
);

 const setSelectedDeliveryAddress = useCallback((address) => {
   const cleanAddress = String(address || "").trim();

   setSelectedDeliveryAddressState(cleanAddress);

   try {
     // Remove any old localStorage value
     localStorage.removeItem("selectedDeliveryAddress");

     if (cleanAddress) {
       sessionStorage.setItem("selectedDeliveryAddress", cleanAddress);
     } else {
       sessionStorage.removeItem("selectedDeliveryAddress");
     }
   } catch (error) {
     console.error("Unable to save selected delivery address:", error);
   }
 }, []);

 const setSelectedDeliveryLocation = useCallback((location) => {
   const latitude = String(location?.latitude ?? location?.lat ?? "").trim();
   const longitude = String(location?.longitude ?? location?.lng ?? "").trim();

   setSelectedDeliveryLocationState({
     latitude,
     longitude,
   });

   try {
     // Old localStorage values bhi clean kar do
     localStorage.removeItem("selectedDeliveryLatitude");
     localStorage.removeItem("selectedDeliveryLongitude");

     if (latitude && longitude) {
       sessionStorage.setItem("selectedDeliveryLatitude", latitude);
       sessionStorage.setItem("selectedDeliveryLongitude", longitude);
     } else {
       sessionStorage.removeItem("selectedDeliveryLatitude");
       sessionStorage.removeItem("selectedDeliveryLongitude");
     }
   } catch (error) {
     console.error("Unable to save selected delivery location:", error);
   }
 }, []);

  /* =====================================================
     ORDER TYPE AND PLATFORM
  ===================================================== */

  const [selectedOrderType, setSelectedOrderTypeState] = useState(() => {
    const savedOrderType = getSessionValue("selectedOrderType", "");

    return savedOrderType || DEFAULT_ORDER_TYPE;
  });

  const [selectedPlatform, setSelectedPlatformState] = useState(() =>
    getSessionValue("selectedPlatform", ""),
  );

  const updateSelectedOrderType = useCallback((orderType) => {
    const cleanOrderType = String(orderType || "").trim();

    const finalOrderType = cleanOrderType || DEFAULT_ORDER_TYPE;

    setSelectedOrderTypeState(finalOrderType);

    try {
      sessionStorage.setItem("selectedOrderType", finalOrderType);
    } catch {
      // Session storage unavailable.
    }
  }, []);

  const updateSelectedPlatform = useCallback((platform) => {
    const cleanPlatform = String(platform || "").trim();

    setSelectedPlatformState(cleanPlatform);

    try {
      sessionStorage.setItem("selectedPlatform", cleanPlatform);
    } catch {
      // Session storage unavailable.
    }
  }, []);

  /* =====================================================
     AUTH HEADERS
  ===================================================== */

  const basicAuth = useMemo(() => {
    if (!salesUsername || !salesPassword) {
      return "";
    }

    return `Basic ${btoa(`${salesUsername}:${salesPassword}`)}`;
  }, [salesUsername, salesPassword]);

  const packkyBasicAuth = useMemo(() => {
    if (!packkyUsername || !packkyPassword) {
      return "";
    }

    return `Basic ${btoa(`${packkyUsername}:${packkyPassword}`)}`;
  }, [packkyUsername, packkyPassword]);

  const anlookImageBasicAuth = useMemo(() => {
    if (!anlookImageUsername || !anlookImagePassword) {
      return "";
    }

    return `Basic ${btoa(`${anlookImageUsername}:${anlookImagePassword}`)}`;
  }, [anlookImageUsername, anlookImagePassword]);

  /* =====================================================
     ANLOOK API INSTANCE
  ===================================================== */

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: baseUrl,

      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",

        ...(basicAuth
          ? {
              Authorization: basicAuth,
            }
          : {}),

        ...(appName
          ? {
              "X-App-Name": appName,
            }
          : {}),
      },

      timeout: 30000,
    });

    instance.interceptors.request.use(
      (config) => {
        return config;
      },
      (error) => Promise.reject(error),
    );

    return instance;
  }, [baseUrl, basicAuth, appName]);

  /* =====================================================
     PACKKY API INSTANCE
  ===================================================== */

  const packkyApi = useMemo(() => {
    return axios.create({
      baseURL: packkyBaseUrl,

      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",

        ...(packkyBasicAuth
          ? {
              Authorization: packkyBasicAuth,
            }
          : {}),
      },

      timeout: 30000,
    });
  }, [packkyBaseUrl, packkyBasicAuth]);

  /* =====================================================
   ANLOOK PHP IMAGE API INSTANCE
===================================================== */

  const anlookImageApi = useMemo(() => {
    return axios.create({
      baseURL: anlookImageBaseUrl,

      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",

        ...(anlookImageBasicAuth
          ? {
              Authorization: anlookImageBasicAuth,
            }
          : {}),
      },

      timeout: 60000,
    });
  }, [anlookImageBaseUrl, anlookImageBasicAuth]);

  /* =====================================================
     CONTEXT VALUE
  ===================================================== */

 const contextValue = useMemo(
   () => ({
     // APIs
     api,
     packkyApi,
     anlookImageApi,

     // Config
     baseUrl,
     packkyBaseUrl,
     anlookImageBaseUrl,
     appName,
     googleMapsApiKey,

     // Config
     baseUrl,
     packkyBaseUrl,
     appName,
     googleMapsApiKey,

     // Authentication
     user,
     setUser,
     logoutUser,
     authLoading,
     isAuthenticated,

     // Categories
     selectedCategoryId,
     selectedCategoryIds,
     selectedCategoryName,
     selectCategory,
     clearSelectedCategory,

     // Restaurant
     selectedRestaurant,
     selectRestaurant,
     clearSelectedRestaurant,

     // Delivery Address
     selectedDeliveryAddress,
     setSelectedDeliveryAddress,

     selectedDeliveryLocation,
     setSelectedDeliveryLocation,

     // Order
     selectedOrderType,
     selectedPlatform,
     updateSelectedOrderType,
     updateSelectedPlatform,
   }),
   [
     api,
     packkyApi,
     anlookImageApi,

     baseUrl,
     packkyBaseUrl,
     anlookImageBaseUrl,
     appName,
     googleMapsApiKey,

     baseUrl,
     packkyBaseUrl,
     appName,
     googleMapsApiKey,

     user,
     setUser,
     logoutUser,
     authLoading,
     isAuthenticated,

     selectedCategoryId,
     selectedCategoryIds,
     selectedCategoryName,
     selectCategory,
     clearSelectedCategory,

     selectedRestaurant,
     selectRestaurant,
     clearSelectedRestaurant,

     // Delivery Address
     selectedDeliveryAddress,
     setSelectedDeliveryAddress,
     selectedDeliveryLocation,
     setSelectedDeliveryLocation,

     selectedOrderType,
     selectedPlatform,
     updateSelectedOrderType,
     updateSelectedPlatform,
   ],
 );

  return (
    <GlobalContext.Provider value={contextValue}>
      {children}
    </GlobalContext.Provider>
  );
};;

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);

  if (!context) {
    throw new Error("useGlobalContext must be used inside GlobalProvider");
  }

  return context;
};
